// Phase 29.2 — Manager approvals for Start-Call geo-exceptions.
//
// SE requests an exception when blocked by the start-call radius. This screen
// lists pending requests scoped to the current approver and lets them
// approve/reject. Audit log entries are written server-side on every action.
import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, XCircle, Loader2, Filter } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "@/components/ui/tabs";
import PageHeader from "@/components/PageHeader";

const STATUSES = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
];

export default function GeoExceptionsPage() {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/visits/geo-exception-requests", {
        params: { status },
      });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, action) => {
    setActing(id);
    try {
      await api.post(`/visits/geo-exception-requests/${id}/${action}`);
      toast.success(`Request ${action}d`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setActing(null);
    }
  };

  const pendingCount = useMemo(
    () => (status === "pending" ? items.length : null),
    [items, status]
  );

  return (
    <div className="space-y-4 max-w-3xl" data-testid="geo-exceptions-page">
      <PageHeader
        breadcrumbs={[
          { label: "Approvals", to: "/" },
          { label: "Geo-exceptions" },
        ]}
        title="Start-Call geo-exceptions"
        subtitle="Approve or reject SE requests to check in outside the start-call radius."
        favorite={{
          key: "admin.geo-exceptions",
          type: "screen",
          label: "Geo-exceptions",
          route: "/admin/geo-exceptions",
        }}
        testid="geo-exceptions-page-header"
      />

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          {STATUSES.map((s) => (
            <TabsTrigger
              key={s.key} value={s.key}
              data-testid={`geo-exceptions-tab-${s.key}`}
            >
              {s.label}
              {s.key === "pending" && pendingCount !== null && pendingCount > 0
                ? ` (${pendingCount})` : ""}
            </TabsTrigger>
          ))}
        </TabsList>
        {STATUSES.map((s) => (
          <TabsContent key={s.key} value={s.key} className="mt-3 space-y-2">
            {loading && (
              <div className="py-6 text-center text-neutral-400">
                <Loader2 className="h-5 w-5 inline animate-spin" />
              </div>
            )}
            {!loading && items.length === 0 && (
              <div className="py-10 text-center text-neutral-400 text-sm">
                No {s.key} requests.
              </div>
            )}
            {!loading && items.map((r) => (
              <RequestCard
                key={r.id}
                req={r}
                busy={acting === r.id}
                onApprove={() => decide(r.id, "approve")}
                onReject={() => decide(r.id, "reject")}
                canAct={s.key === "pending"}
              />
            ))}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}

function RequestCard({ req, busy, onApprove, onReject, canAct }) {
  const ts = req.requesting_ts
    ? new Date(req.requesting_ts).toLocaleString()
    : "";
  return (
    <Card className="border-neutral-200 rounded-2xl"
          data-testid={`geo-exception-row-${req.id.slice(0, 8)}`}>
      <CardContent className="p-4 flex items-start gap-3">
        <div className="flex-1 min-w-0 space-y-1">
          <div className="text-sm font-semibold text-neutral-900">
            {req.user_name || req.user_id?.slice(0, 8)} · outlet {req.outlet_id?.slice(0, 8)}
          </div>
          <div className="text-xs text-neutral-600">
            <span className="font-semibold text-red-700">{req.distance_m}m</span>
            {" "}from outlet · GPS ±{req.gps?.accuracy_m ?? "?"}m
          </div>
          {req.reason && (
            <div className="text-xs text-neutral-500 italic">
              "{req.reason}"
            </div>
          )}
          <div className="text-[11px] text-neutral-400 font-mono">{ts}</div>
          {req.status !== "pending" && (
            <div className="text-[11px] text-neutral-500">
              {req.status} · decided {req.decided_at
                ? new Date(req.decided_at).toLocaleString() : ""}
            </div>
          )}
        </div>
        {canAct && (
          <div className="flex flex-col gap-2 shrink-0">
            <Button size="sm" onClick={onApprove} disabled={busy}
                    className="bg-green-600 hover:bg-green-700 text-white h-9 px-3 rounded-lg"
                    data-testid={`geo-exception-approve-${req.id.slice(0, 8)}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" />
                    : <CheckCircle2 className="h-4 w-4 mr-1" />}Approve
            </Button>
            <Button size="sm" onClick={onReject} disabled={busy}
                    variant="outline"
                    className="border-red-300 text-red-700 h-9 px-3 rounded-lg"
                    data-testid={`geo-exception-reject-${req.id.slice(0, 8)}`}>
              <XCircle className="h-4 w-4 mr-1" />Reject
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
